(() => {
  if (window.__greencreekInstafeedLightboxV12) return;
  window.__greencreekInstafeedLightboxV12 = true;

  const sectionSelector = '[data-greencreek-instafeed-section]';
  const wrapperSelector = '.instafeed-modals-wrapper';
  const lightboxSelector = '.lightbox-instagram';
  const inlineVideoSelector = '.auto-loop-videos';
  const overlayClass = 'greencreek-instafeed-overlay';
  const captionClass = 'greencreek-instafeed-caption';
  const expandedClass = 'greencreek-caption-expanded';
  const overlayVersion = 'gc-instafeed-v12';

  const observedWrappers = new WeakSet();
  const boundSections = new WeakSet();
  const frozenFeedVideos = new WeakSet();
  const mediaSources = new WeakMap();
  const wrapperFrames = new WeakMap();
  let connectTimer = 0;

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const hasSection = () => Boolean(document.querySelector(sectionSelector));

  const getSectionVideos = () =>
    Array.from(document.querySelectorAll(`${sectionSelector} ${inlineVideoSelector}`)).filter(
      (video) => video instanceof HTMLVideoElement
    );

  const freezeFeedVideo = (video) => {
    if (!(video instanceof HTMLVideoElement) || !isMobile()) return;

    video.preload = 'none';
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.muted = true;
    video.playsInline = true;
    video.pause();

    if (frozenFeedVideos.has(video)) return;
    frozenFeedVideos.add(video);

    video.addEventListener('play', () => {
      if (!isMobile()) return;
      requestAnimationFrame(() => video.pause());
    });
  };

  const freezeMobileFeedVideos = () => {
    if (!isMobile()) return;
    getSectionVideos().forEach(freezeFeedVideo);
  };

  const observeFeedVideosWithin = (root) => {
    if (!isMobile()) return;
    if (root instanceof HTMLVideoElement && root.matches(inlineVideoSelector)) freezeFeedVideo(root);
    root.querySelectorAll?.(inlineVideoSelector).forEach(freezeFeedVideo);
  };

  const findPermalink = (lightbox) =>
    lightbox.querySelector(':scope > .description .post-date a[href*="instagram.com"]') ||
    Array.from(lightbox.querySelectorAll('a[href*="instagram.com"]')).find((link) =>
      /instagram\.com\/(?:p|reel|tv)\//i.test(link.href)
    ) ||
    null;

  const getAccountData = (lightbox, header) => {
    const sourceHeader = header instanceof HTMLElement ? header : lightbox.querySelector(':scope > .instafeed-header');
    const profileLink =
      sourceHeader?.querySelector('a.follow[href*="instagram.com"]') ||
      lightbox.querySelector('a[href*="instagram.com/greencreek_jewelry"]') ||
      null;

    let name = sourceHeader?.querySelector('.instafeed-fullname')?.textContent?.trim() || '';
    const avatar =
      sourceHeader?.querySelector('.instafeed-header-logo img, .profile-picture')?.getAttribute('src') ||
      lightbox.querySelector('.profile-picture')?.getAttribute('src') ||
      '';

    if (!name && profileLink instanceof HTMLAnchorElement) {
      try {
        const pathname = new URL(profileLink.href).pathname.replace(/^\/+|\/+$/g, '');
        if (pathname && !/^(p|reel|tv)\//i.test(pathname)) name = pathname.split('/')[0] || '';
      } catch (_) {}
    }

    return {
      name: name || 'greencreek_jewelry',
      avatar,
      profileHref:
        profileLink instanceof HTMLAnchorElement && profileLink.href
          ? profileLink.href
          : 'https://www.instagram.com/greencreek_jewelry',
    };
  };

  const buildAccount = ({ name, avatar, profileHref }) => {
    const account = document.createElement('a');
    account.className = 'greencreek-instafeed-account';
    account.href = profileHref;
    account.target = '_blank';
    account.rel = 'noopener noreferrer';
    account.setAttribute('aria-label', `Open @${name} profile`);

    if (avatar) {
      const avatarWrap = document.createElement('span');
      avatarWrap.className = 'greencreek-instafeed-account-avatar';

      const image = document.createElement('img');
      image.src = avatar;
      image.alt = `${name} Instagram profile picture`;
      image.loading = 'eager';
      image.decoding = 'async';
      avatarWrap.appendChild(image);
      account.appendChild(avatarWrap);
    }

    const nameNode = document.createElement('span');
    nameNode.className = 'greencreek-instafeed-account-name';
    nameNode.textContent = name;
    account.appendChild(nameNode);
    return account;
  };

  const ensureAccount = (lightbox, overlay, header) => {
    const data = getAccountData(lightbox, header);
    const signature = [data.name, data.avatar, data.profileHref].join('|');
    const current = overlay.querySelector(':scope > .greencreek-instafeed-account');

    if (current instanceof HTMLElement && current.dataset.accountSignature === signature) return;
    current?.remove();

    const account = buildAccount(data);
    account.dataset.accountSignature = signature;
    overlay.prepend(account);
  };

  const makeButton = (type, original, wrapper) => {
    if (!(original instanceof HTMLElement)) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = `greencreek-instafeed-control greencreek-instafeed-control--${type}`;
    button.setAttribute('aria-label', type === 'close' ? 'Close Instagram post' : `${type} Instagram post`);

    if (type === 'close') {
      button.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5l14 14M19 5L5 19"/></svg>';
    } else {
      const path = type === 'previous' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7';
      button.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
    }

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      original.click();

      if (type !== 'close') scheduleWrapperRefresh(wrapper);
    });

    return button;
  };

  const getMediaSource = (media) => media?.querySelector(':scope > video, :scope > img');

  const getMediaRatio = (source) => {
    if (source instanceof HTMLVideoElement && source.videoWidth > 0 && source.videoHeight > 0) {
      return source.videoWidth / source.videoHeight;
    }
    if (source instanceof HTMLImageElement && source.naturalWidth > 0 && source.naturalHeight > 0) {
      return source.naturalWidth / source.naturalHeight;
    }
    return 9 / 16;
  };

  const syncMobileMediaSize = (lightbox) => {
    const media = lightbox.querySelector(':scope > .instafeed-post-image');
    if (!(media instanceof HTMLElement)) return;

    if (!isMobile()) {
      if (media.style.getPropertyValue('--gc-instafeed-media-width')) media.style.removeProperty('--gc-instafeed-media-width');
      if (media.style.getPropertyValue('--gc-instafeed-media-height')) media.style.removeProperty('--gc-instafeed-media-height');
      return;
    }

    const ratio = Math.max(0.35, Math.min(2.2, getMediaRatio(getMediaSource(media))));
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    const maxWidth = Math.max(240, viewportWidth - 16);
    const maxHeight = Math.max(360, viewportHeight - 24);

    let width = Math.min(maxWidth, maxHeight * ratio);
    let height = width / ratio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }

    const widthValue = `${Math.round(width)}px`;
    const heightValue = `${Math.round(height)}px`;

    if (media.style.getPropertyValue('--gc-instafeed-media-width') !== widthValue) {
      media.style.setProperty('--gc-instafeed-media-width', widthValue);
    }
    if (media.style.getPropertyValue('--gc-instafeed-media-height') !== heightValue) {
      media.style.setProperty('--gc-instafeed-media-height', heightValue);
    }
  };

  const watchMedia = (lightbox) => {
    const media = lightbox.querySelector(':scope > .instafeed-post-image');
    const source = getMediaSource(media);
    if (!(media instanceof HTMLElement) || !(source instanceof HTMLElement)) return;

    const sourceKey = source instanceof HTMLVideoElement ? source.currentSrc || source.src : source.currentSrc || source.src;
    if (mediaSources.get(media) === sourceKey) return;
    mediaSources.set(media, sourceKey);

    const update = () => requestAnimationFrame(() => syncMobileMediaSize(lightbox));

    if (source instanceof HTMLVideoElement) {
      source.addEventListener('loadedmetadata', update, { once: true });
      source.addEventListener('loadeddata', update, { once: true });
    } else if (source instanceof HTMLImageElement) {
      source.addEventListener('load', update, { once: true });
    }

    update();
  };

  const buildOverlay = (lightbox) => {
    const wrapper = lightbox.closest(wrapperSelector);
    const header = lightbox.querySelector(':scope > .instafeed-header');
    const media = lightbox.querySelector(':scope > .instafeed-post-image');
    const description = lightbox.querySelector(':scope > .description');
    const bottomBar = lightbox.querySelector(':scope > .instafeed-bottom-bar');

    if (!(media instanceof HTMLElement) || !(description instanceof HTMLElement)) return;

    lightbox.classList.add('greencreek-instafeed-lightbox');
    wrapper?.classList.add('greencreek-instafeed-stage');
    header?.classList.add('greencreek-instafeed-original-header');
    description.classList.add('greencreek-instafeed-original-description');
    bottomBar?.classList.add('greencreek-instafeed-original-bottom-bar');
    media.classList.add('greencreek-instafeed-media-card');

    freezeMobileFeedVideos();

    const accountData = getAccountData(lightbox, header);
    const captionText = description.querySelector('.instafeed-caption')?.textContent?.trim() || '';
    const permalink = findPermalink(lightbox)?.href || '';
    const signature = [accountData.name, accountData.avatar, accountData.profileHref, captionText, permalink].join('|');

    let overlay = media.querySelector(`:scope > .${overlayClass}`);
    const validOverlay =
      overlay instanceof HTMLElement &&
      overlay.dataset.version === overlayVersion &&
      overlay.dataset.signature === signature;

    if (!validOverlay) {
      overlay?.remove();
      overlay = document.createElement('div');
      overlay.className = overlayClass;
      overlay.dataset.version = overlayVersion;
      overlay.dataset.signature = signature;

      const sourceLink = findPermalink(lightbox);
      if (sourceLink instanceof HTMLAnchorElement) {
        const viewPost = document.createElement('a');
        viewPost.className = 'greencreek-instafeed-view-post';
        viewPost.href = sourceLink.href;
        viewPost.target = '_blank';
        viewPost.rel = 'noopener noreferrer';
        viewPost.textContent = 'View post';
        viewPost.setAttribute('aria-label', 'View this post on Instagram');
        overlay.appendChild(viewPost);
      }

      if (captionText) {
        const caption = document.createElement('div');
        caption.className = captionClass;
        caption.textContent = captionText;
        caption.tabIndex = 0;
        caption.setAttribute('role', 'button');
        caption.setAttribute('aria-expanded', 'false');
        caption.setAttribute('aria-label', 'Expand Instagram caption');
        overlay.appendChild(caption);
      }

      const closeOriginal = bottomBar?.querySelector('.instafeed-close-button');
      const previousOriginal = bottomBar?.querySelector('.instafeed-post-control-button.arrow-up');
      const nextOriginal = bottomBar?.querySelector('.instafeed-post-control-button.arrow-down');

      const previous = makeButton('previous', previousOriginal, wrapper);
      const next = makeButton('next', nextOriginal, wrapper);
      const close = makeButton('close', closeOriginal, wrapper);

      if (previous) overlay.appendChild(previous);
      if (next) overlay.appendChild(next);
      if (close) overlay.appendChild(close);
      media.appendChild(overlay);
    }

    ensureAccount(lightbox, overlay, header);
    syncMobileMediaSize(lightbox);
    watchMedia(lightbox);
  };

  const enhanceWrapper = (wrapper) => {
    if (!(wrapper instanceof HTMLElement) || !hasSection()) return;
    wrapper.querySelectorAll(lightboxSelector).forEach(buildOverlay);
  };

  const scheduleWrapperRefresh = (wrapper) => {
    if (!(wrapper instanceof HTMLElement)) return;

    const oldFrame = wrapperFrames.get(wrapper);
    if (oldFrame) cancelAnimationFrame(oldFrame);

    const frame = requestAnimationFrame(() => {
      wrapperFrames.delete(wrapper);
      enhanceWrapper(wrapper);
    });
    wrapperFrames.set(wrapper, frame);

    [120, 350, 800].forEach((delay) => {
      window.setTimeout(() => enhanceWrapper(wrapper), delay);
    });
  };

  const toggleCaption = (caption) => {
    const expanded = caption.classList.toggle(expandedClass);
    caption.setAttribute('aria-expanded', String(expanded));
    caption.setAttribute('aria-label', expanded ? 'Collapse Instagram caption' : 'Expand Instagram caption');
  };

  const bindWrapper = (wrapper) => {
    if (!(wrapper instanceof HTMLElement) || observedWrappers.has(wrapper)) return;
    observedWrappers.add(wrapper);

    wrapper.addEventListener('click', (event) => {
      const caption = event.target.closest?.(`.${captionClass}`);
      if (!caption) return;
      event.preventDefault();
      event.stopPropagation();
      toggleCaption(caption);
    });

    wrapper.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const caption = event.target.closest?.(`.${captionClass}`);
      if (!caption) return;
      event.preventDefault();
      event.stopPropagation();
      toggleCaption(caption);
    });

    const observer = new MutationObserver((mutations) => {
      const hasExternalNodeChange = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => node instanceof Element && !node.closest?.(`.${overlayClass}`)) ||
        Array.from(mutation.removedNodes).some((node) => node instanceof Element)
      );

      if (!hasExternalNodeChange) return;
      scheduleWrapperRefresh(wrapper);
    });

    observer.observe(wrapper, {
      childList: true,
      subtree: true,
    });

    scheduleWrapperRefresh(wrapper);
  };

  const connectWrappers = () => {
    if (!hasSection()) return;
    document.querySelectorAll(wrapperSelector).forEach(bindWrapper);
  };

  const scheduleWrapperDiscovery = () => {
    window.clearTimeout(connectTimer);
    [0, 120, 350, 800, 1500].forEach((delay) => {
      window.setTimeout(connectWrappers, delay);
    });
    connectTimer = window.setTimeout(() => {}, 1500);
  };

  const bindSection = (section) => {
    if (!(section instanceof HTMLElement) || boundSections.has(section)) return;
    boundSections.add(section);

    observeFeedVideosWithin(section);

    const sectionObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) observeFeedVideosWithin(node);
        });
      });
      freezeMobileFeedVideos();
    });

    sectionObserver.observe(section, {
      childList: true,
      subtree: true,
    });

    section.addEventListener('click', () => {
      freezeMobileFeedVideos();
      scheduleWrapperDiscovery();
    }, { passive: true });
    section.addEventListener('keydown', scheduleWrapperDiscovery);
  };

  const initSections = () => {
    document.querySelectorAll(sectionSelector).forEach(bindSection);
    scheduleWrapperDiscovery();
    window.setTimeout(freezeMobileFeedVideos, 300);
    window.setTimeout(freezeMobileFeedVideos, 1000);
    window.setTimeout(freezeMobileFeedVideos, 2200);
  };

  const syncVisibleLightboxes = () => {
    if (!hasSection()) return;
    document.querySelectorAll(`${wrapperSelector} ${lightboxSelector}`).forEach((lightbox) => {
      syncMobileMediaSize(lightbox);
    });
    freezeMobileFeedVideos();
  };

  window.addEventListener('resize', syncVisibleLightboxes, { passive: true });
  window.visualViewport?.addEventListener('resize', syncVisibleLightboxes, { passive: true });

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.(sectionSelector) ||
      (event.target?.matches?.(sectionSelector) ? event.target : null);
    if (section) {
      bindSection(section);
      scheduleWrapperDiscovery();
    }
  });

  initSections();
})();